import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { DEFAULT_ORG_GROUP, QR_API } from '../core/constants';
import { ApiEnvelope, QrBanner, QrMenuResponse, QrOrgResponse, QrPromosResponse } from './qr.types';
import {
  PaymentProvider,
  QrAddItemsRequest,
  QrCreateOrderRequest,
  QrOrder,
  QrTable,
} from './table.types';

/** Склейка сегментов с ровно одним слешем; у apiUrl нет ведущих слешей, схема не страдает. */
function joinUrl(...parts: string[]): string {
  return parts
    .map((p) => p.replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/');
}

/** На пустом конверте бросает явную ошибку — её ловит catchError в menu-state (-> notFound). */
function unwrap<T>(res: ApiEnvelope<T>): T {
  if (res?.response == null) {
    throw new Error(res?.message ?? 'Empty QR API response');
  }
  return res.response;
}

/** Граница доверия: строка уедет в location — ничего, кроме https-URL, не пропускаем. */
export function readLink(raw: unknown): string {
  if (typeof raw !== 'string' || !raw.startsWith('https://')) {
    throw new Error('Invalid checkout link');
  }
  return raw;
}

/** Провайдеры, у которых есть кнопка (у UZUM ни логотипа, ни лейбла). PayButton.provider принимает
 *  ровно их — лишний провайдер здесь упрётся в компилятор в шаблоне счёта. Порядок кнопок задаёт
 *  сайт, а не ответ: так они не прыгают между заходами. */
const PAY_PROVIDERS = ['CLICK', 'PAYME'] as const;
export type PayProvider = (typeof PAY_PROVIDERS)[number];

/** Граница доверия: из ответа берём только знакомых провайдеров, остальное — молча мимо. */
function readProviders(raw: unknown): PayProvider[] {
  const list: unknown[] = Array.isArray(raw) ? raw : [];
  return PAY_PROVIDERS.filter((p) => list.includes(p));
}

@Service()
export class QrService {
  private http = inject(HttpClient);
  private base = joinUrl(environment.apiUrl, QR_API.BASE);
  getOrg(group = DEFAULT_ORG_GROUP): Observable<QrOrgResponse> {
    return this.http
      .get<ApiEnvelope<QrOrgResponse>>(joinUrl(this.base, QR_API.ORG), { headers: { group } })
      .pipe(map((res) => unwrap(res)));
  }
  /** Меню скоупится QR-токеном (qrTokenInterceptor) и, в отличие от org, не требует group. */
  getMenu(): Observable<QrMenuResponse> {
    return this.http
      .get<ApiEnvelope<QrMenuResponse>>(joinUrl(this.base, QR_API.MENU))
      .pipe(map((res) => unwrap(res)));
  }

  /** Пустой `response` — это «акций нет», не ошибка: unwrap здесь не подходит. */
  getPromos(tableUuid: string | null): Observable<QrPromosResponse | null> {
    const params = tableUuid ? new HttpParams().set('tableUuid', tableUuid) : undefined;
    return this.http
      .get<ApiEnvelope<QrPromosResponse | null>>(joinUrl(this.base, QR_API.PROMOS), { params })
      .pipe(map((res) => res?.response ?? null));
  }

  /** Пустой `response` — это «баннеров нет», не ошибка: unwrap здесь не подходит. */
  getBanners(): Observable<(QrBanner | null)[] | null> {
    return this.http
      .get<ApiEnvelope<(QrBanner | null)[] | null>>(joinUrl(this.base, QR_API.BANNER))
      .pipe(map((res) => res?.response ?? null));
  }

  /** Онлайн-оплаты, подключённые у заведения. Пустой `response` — «онлайн-оплаты нет», не ошибка. */
  getPaymentProviders(): Observable<PayProvider[]> {
    return this.http
      .get<ApiEnvelope<unknown>>(joinUrl(this.base, QR_API.PAYMENT_PROVIDERS))
      .pipe(map((res) => readProviders(res?.response)));
  }

  /** Table identity (uuid + display name) so the guest can see which table they're at. */
  getTable(tableUuid: string): Observable<QrTable> {
    return this.http
      .get<ApiEnvelope<QrTable>>(joinUrl(this.base, QR_API.TABLE, tableUuid))
      .pipe(map((res) => unwrap(res)));
  }

  /** Active orders for a table (token header via interceptor; empty array when none). */
  getTableOrders(tableUuid: string): Observable<QrOrder[]> {
    return this.http
      .get<ApiEnvelope<QrOrder[]>>(joinUrl(this.base, QR_API.TABLE, tableUuid, QR_API.ORDERS))
      .pipe(map((res) => unwrap(res)));
  }

  /**
   * Requests the waiter of an order (`?orderId=` — the backend pushes the order's own waiter).
   * Resolves on 200; the caller handles 400 (spam guard: one call per table per 30s). Response
   * body is `{response:null}`, so we don't unwrap it.
   */
  callWaiter(tableUuid: string, orderId: number): Observable<void> {
    const url = joinUrl(this.base, QR_API.TABLE, tableUuid, QR_API.CALL_WAITER);
    const params = new HttpParams().set('orderId', orderId);
    return this.http.post<ApiEnvelope<null>>(url, {}, { params }).pipe(map(() => undefined));
  }

  /**
   * Appends guest-picked positions to an active order. Emits the updated order with ALL its
   * items — the new ones arrive as `PENDING_APPROVAL` (not billed, not sent to the kitchen
   * until a waiter approves). The caller handles 400 (validation / 10s cooldown shared by the
   * whole table) and 404 (order gone or no longer active).
   */
  addOrderItems(tableUuid: string, orderId: number, body: QrAddItemsRequest): Observable<QrOrder> {
    const url = joinUrl(
      this.base,
      QR_API.TABLE,
      tableUuid,
      QR_API.ORDERS,
      String(orderId),
      QR_API.ITEMS,
    );
    return this.http.post<ApiEnvelope<QrOrder>>(url, body).pipe(map((res) => unwrap(res)));
  }

  /**
   * Opens a new order on the table from the cart. Emits the created order — numbered already,
   * every item `PENDING_APPROVAL` until a waiter approves. Same 400 (validation / the table's
   * 10s cooldown) and 404 (unknown table) handling as addOrderItems.
   */
  createOrder(tableUuid: string, body: QrCreateOrderRequest): Observable<QrOrder> {
    const url = joinUrl(this.base, QR_API.TABLE, tableUuid, QR_API.ORDERS);
    return this.http.post<ApiEnvelope<QrOrder>>(url, body).pipe(map((res) => unwrap(res)));
  }

  /**
   * Requests the bill for an active order. A 200 (`{response:null}`) means the request reached
   * the waiter (FCM push); the actual confirmation arrives later as a BILL_CONFIRMED socket
   * event — no automatic stub. The caller handles 400 (spam guard: one request per table per
   * 30s) and 404 (order gone, not on this table, or already closed/paid).
   */
  requestBill(tableUuid: string, orderId: number): Observable<void> {
    const url = joinUrl(
      this.base,
      QR_API.TABLE,
      tableUuid,
      QR_API.ORDERS,
      String(orderId),
      QR_API.REQUEST_BILL,
    );
    return this.http.post<ApiEnvelope<null>>(url, {}).pipe(map(() => undefined));
  }

  /** URL страницы оплаты выбранного провайдера; сумм здесь нет — их в ссылку кладёт сервер. */
  checkoutLink(
    tableUuid: string,
    orderId: number,
    provider: PaymentProvider,
    returnUrl: string,
  ): Observable<string> {
    const url = joinUrl(
      this.base,
      QR_API.TABLE,
      tableUuid,
      QR_API.ORDERS,
      String(orderId),
      QR_API.CHECKOUT_LINK,
    );
    const params = new HttpParams().set('provider', provider).set('returnUrl', returnUrl);
    return this.http
      .get<ApiEnvelope<unknown>>(url, { params })
      .pipe(map((res) => readLink(unwrap(res))));
  }
}
